import { resolveAccessToken } from "@/lib/authToken";
import { userProfileService } from "@/services/userProfileService";

export interface TtsVoiceOption {
  voiceId: string;
  name: string;
  category: string | null;
  labels: Record<string, string>;
  previewUrl: string | null;
}

/** @deprecated Use TtsVoiceOption */
export type ElevenLabsVoiceOption = TtsVoiceOption;

export interface TtsPreferences {
  ttsVoiceUri?: string;
  ttsSpeechRate?: number;
  ttsVoiceId?: string;
  /** @deprecated migrated to ttsVoiceId */
  elevenlabsVoiceId?: string;
}

const VOICES_CACHE_TTL_MS = 5 * 60_000;
const DEFAULT_TTS_VOICE_ID = "claire";

let voicesCache: { voices: TtsVoiceOption[]; fetchedAt: number } | null = null;
let voicesUnavailable = false;
let inflightVoices: Promise<TtsVoiceOption[]> | null = null;

export function voiceDisplayName(voice: TtsVoiceOption): string {
  return voice.name;
}

/** Read saved TTS voice id, including legacy ElevenLabs preference key. */
export function resolveTtsVoiceId(prefs: Record<string, unknown> | undefined): string | undefined {
  if (!prefs) return undefined;
  if (typeof prefs.ttsVoiceId === "string" && prefs.ttsVoiceId.trim()) {
    return prefs.ttsVoiceId;
  }
  if (typeof prefs.elevenlabsVoiceId === "string" && prefs.elevenlabsVoiceId.trim()) {
    return DEFAULT_TTS_VOICE_ID;
  }
  return undefined;
}

export const ttsService = {
  isUnavailable(): boolean {
    return voicesUnavailable;
  },

  async getVoices(): Promise<TtsVoiceOption[]> {
    if (voicesUnavailable) return [];
    if (voicesCache && Date.now() - voicesCache.fetchedAt < VOICES_CACHE_TTL_MS) {
      return voicesCache.voices;
    }
    if (inflightVoices) return inflightVoices;

    inflightVoices = (async () => {
      try {
        const token = await resolveAccessToken();
        const res = await fetch("/api/tts/voices", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 503) {
          voicesUnavailable = true;
          return [];
        }
        if (!res.ok) return voicesCache?.voices ?? [];
        const json = (await res.json()) as { voices?: TtsVoiceOption[] };
        const voices = Array.isArray(json?.voices) ? json.voices : [];
        voicesCache = { voices, fetchedAt: Date.now() };
        return voices;
      } catch {
        return voicesCache?.voices ?? [];
      } finally {
        inflightVoices = null;
      }
    })();
    return inflightVoices;
  },

  async synthesize(text: string, voiceId: string, speed?: number): Promise<Blob> {
    const token = await resolveAccessToken();
    const res = await fetch("/api/tts/synthesize", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ text, voiceId, ...(speed && speed !== 1 ? { speed } : {}) }),
    });
    if (!res.ok) {
      let message = "Audio generation failed";
      try {
        const json = (await res.json()) as { error?: string };
        if (typeof json?.error === "string" && json.error.trim()) message = json.error;
      } catch {
        // non-JSON error body — keep the generic message
      }
      throw new Error(message);
    }
    return res.blob();
  },

  async savePreferences(prefs: TtsPreferences): Promise<void> {
    const profile = await userProfileService.getUserProfile();
    const existing = (profile?.preferences as Record<string, unknown>) || {};
    const { elevenlabsVoiceId: _legacy, ...rest } = prefs;
    await userProfileService.upsertUserProfile({
      preferences: { ...existing, ...rest },
    } as Parameters<typeof userProfileService.upsertUserProfile>[0]);
  },
};
